/* eslint-disable no-param-reassign */
import {camelCase} from 'camel-case';
import {StoreSettings, SettingsParamsType} from 'models/storeSettings';
import {StoreDetails} from '../models/storeDetails';
import {StoreQueryDetails} from '../api/storesApi';
import {StorePurchase} from '../models/storePurchase';
import {OrderSummary, OrderSummaryQuery} from '../models/orderSummary';
import {
  OrderConfirmationResponse,
  OrderConfirmationQuery,
  CampaignQuery
} from '../models/orderConfirmation';
import {StoreProductQuery, StoreProduct} from '../models/storeProduct';
import {UserDetails, UserDetailsQuery} from '../models/user';

type ObjectType = Record<string, unknown>;
type OrderConfirmationResponseKey = keyof OrderConfirmationResponse;

const VALUE_TYPE = {
  OBJECT: 'object',
  STRING: 'string'
};

const NESTED_ARRAY_KEYS = ['capabilities'];

const ORDER_SUMMARY_ITEM_KEYS = [
  'shipping',
  'tax',
  'subtotal',
  'total_collected_online'
];

export const camelCaseTransformer = (res: ObjectType): ObjectType => {
  return Object.keys(res).reduce((prev, curr: string) => {
    prev[camelCase(curr) as string] = res[curr];
    return prev;
  }, {} as ObjectType);
};

const containNumber = (str: string) => {
  return /\d/.test(str) ? str.replace(/_/g, '') : str;
};

export const nestedCamelCaseTransformer = (res: ObjectType): ObjectType => {
  const build = {} as ObjectType;
  let value;

  if (res === null || typeof res !== VALUE_TYPE.OBJECT) {
    return res;
  }

  if (Array.isArray(res)) {
    return res.map(nestedCamelCaseTransformer) as unknown as ObjectType;
  }

  Object.keys(res).forEach(key => {
    const newKey = camelCase(key);
    value = res[key];

    if (Array.isArray(value)) {
      if (
        NESTED_ARRAY_KEYS.includes(key) &&
        typeof value[0] === VALUE_TYPE.STRING
      ) {
        value = value.map(item => camelCase(item));
      }

      value = value.map(nestedCamelCaseTransformer) as unknown as ObjectType;
    }

    if (typeof value === VALUE_TYPE.OBJECT) {
      value = nestedCamelCaseTransformer(value as ObjectType);
    }
    build[containNumber(newKey)] = value;
  });
  return build;
};

export const storesDeserializer = (res: StoreQueryDetails): StoreDetails => {
  return nestedCamelCaseTransformer(res) as unknown as StoreDetails;
};

export const settingsTransformer = (res: SettingsParamsType): StoreSettings => {
  return nestedCamelCaseTransformer(res) as unknown as StoreSettings;
};

export const userTransformer = (res: UserDetailsQuery): UserDetails => {
  return camelCaseTransformer(res) as unknown as UserDetails;
};

export const purchaseDetailTransformer = (
  res: StorePurchase
): StorePurchase => {
  return nestedCamelCaseTransformer(res) as unknown as StorePurchase;
};

export const orderSummaryTransformer = (
  res: OrderSummaryQuery
): OrderSummary => {
  const numericObject = Object.keys(res).reduce((acc, curr: string) => {
    const newVal =
      ORDER_SUMMARY_ITEM_KEYS.indexOf(curr) !== -1
        ? Number(res[curr as keyof OrderSummaryQuery])
        : res[curr as keyof OrderSummaryQuery];
    return {...acc, [curr]: newVal};
  }, {});
  return nestedCamelCaseTransformer(numericObject) as unknown as OrderSummary;
};

export const orderConfirmationTransformer = (
  res: OrderConfirmationQuery
): OrderConfirmationResponse => {
  const numericObject = Object.keys(res.campaign).reduce(
    (acc, curr: OrderConfirmationResponseKey) => {
      const newVal =
        ORDER_SUMMARY_ITEM_KEYS.indexOf(curr) !== -1
          ? Number(res.campaign[curr as keyof CampaignQuery])
          : res.campaign[curr as keyof CampaignQuery];
      return {...acc, [curr]: newVal};
    },
    {}
  );

  const obj = {...res, campaign: numericObject};

  return nestedCamelCaseTransformer(
    obj
  ) as unknown as OrderConfirmationResponse;
};

export const storeProductsTransformer = (
  res: StoreProductQuery
): StoreProduct[] => {
  return nestedCamelCaseTransformer(res) as unknown as StoreProduct[];
};
